import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';

const getAPI = () => getApiUrl();

export default function useVoiceInput() {
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    setVoiceSupported(hasMediaRecorder && hasGetUserMedia);
  }, []);

  const startVoiceInput = useCallback(async (onResult) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Microphone not supported in this browser');
      return;
    }

    if (isListening && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (audioChunksRef.current.length === 0) { setIsListening(false); return; }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        try {
          toast.loading('Processing...', { id: 'transcribe' });
          const formData = new FormData();
          formData.append('file', audioBlob, `recording.${mimeType.split('/')[1]}`);
          const response = await axios.post(`${getAPI()}/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (response.data.text) {
            onResult(response.data.text.trim());
            toast.success('Got it!', { id: 'transcribe' });
          } else {
            toast.error('No speech detected', { id: 'transcribe' });
          }
        } catch (err) {
          console.error('Transcription error:', err);
          toast.error('Failed to transcribe. Please try again.', { id: 'transcribe' });
        }
        setIsListening(false);
      };

      mediaRecorder.onerror = () => {
        toast.error('Recording failed. Please try again.');
        setIsListening(false);
      };

      mediaRecorder.start();
      setIsListening(true);

      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
      }, 10000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone permission.');
      } else {
        toast.error('Could not access microphone. Please try again.');
      }
      setIsListening(false);
    }
  }, [isListening]);

  return { isListening, voiceSupported, startVoiceInput };
}
